import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  memo,
} from "react";
import { View, Text, TextInput, StyleSheet } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Feather, Ionicons } from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import { BlockPlanificationConstants } from "../../../Resources/Constants";

import Tooltip from "../../Components/Tooltip";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import { useToast } from "../../contexts/ToastContext";
import { useDevice } from "../../_layout";

/**
 * WeekHeader - Memoized to prevent re-renders during scroll
 * Only re-renders when props actually change
 */
const WeekHeader = memo(function WeekHeader({
  week,
  editingWeek,
  initialWeekName,
  getWeekLabel,
  commitWeekName,
  cancelWeekNameEdit,
  startEditingWeek,
  onDeleteWeekPress,
  formatWeekInterval,
  isWeekHidden,
  toggleHiddenWeek,
  onDuplicateWeekPress,
  hasWorkouts,
  isCurrentWeek = false,
  t,
  columnWidth = BlockPlanificationConstants.COLUMN_WIDTH,
  isLastWeek = false,
  onConfigureWeeks,
}) {
  const isEditing = editingWeek === week;
  const { showToast } = useToast();
  const { isMobile, isIOS } = useDevice();
  const [draft, setDraft] = useState(
    typeof initialWeekName === "string" ? initialWeekName : "",
  );
  const skipNextBlurCommitRef = useRef(false);
  const pendingBlurTimerRef = useRef(null);
  const weekNameInputRef = useRef(null);
  const [isEyeHovered, setIsEyeHovered] = useState(false);

  useEffect(() => {
    if (!isEditing) return;
    skipNextBlurCommitRef.current = false;
    if (pendingBlurTimerRef.current) {
      clearTimeout(pendingBlurTimerRef.current);
      pendingBlurTimerRef.current = null;
    }
    setDraft(typeof initialWeekName === "string" ? initialWeekName : "");
  }, [isEditing, initialWeekName]);

  const cancelEdit = () => {
    skipNextBlurCommitRef.current = true;
    if (pendingBlurTimerRef.current) {
      clearTimeout(pendingBlurTimerRef.current);
      pendingBlurTimerRef.current = null;
    }
    cancelWeekNameEdit();
  };

  const saveEdit = () => {
    if (pendingBlurTimerRef.current) {
      clearTimeout(pendingBlurTimerRef.current);
      pendingBlurTimerRef.current = null;
    }
    commitWeekName(week, draft);
  };

  const handleBlur = () => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false;
      return;
    }

    // On web, blur fires before the Cancel button's onPress; defer commit.
    if (pendingBlurTimerRef.current) {
      clearTimeout(pendingBlurTimerRef.current);
    }
    pendingBlurTimerRef.current = setTimeout(() => {
      pendingBlurTimerRef.current = null;
      commitWeekName(week, draft);
    }, 120);
  };

  const handleKey = (e) => {
    const key = e?.nativeEvent?.key ?? e?.key;
    if (key === "Enter") {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      saveEdit();
      return;
    }

    if (key === "Escape") {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      cancelEdit();
    }
  };

  const handleToggleHiddenWeek = () => {
    const nextHidden = !isWeekHidden;
    toggleHiddenWeek();
    showToast(
      nextHidden ? "weekHiddenToastTitle-string" : "weekShownToastTitle-string",
      {
        type: "info",
        title: nextHidden
          ? "weekHiddenToastTitle-string"
          : "weekShownToastTitle-string",
        description: nextHidden
          ? "weekHiddenToastDescription-string"
          : "weekShownToastDescription-string",
        duration: BlockPlanificationConstants.TOAST_DURATION_MS,
        variables: {
          weekLabel: getWeekLabel(week),
        },
      },
    );
  };

  // iOS mobile: Use useLayoutEffect for synchronous focus
  useLayoutEffect(() => {
    if (!isEditing) return;
    // Only use useLayoutEffect for iOS mobile
    if (!isMobile || !isIOS) return;
    try {
      const input = weekNameInputRef.current;
      if (input) {
        input.focus?.();
      }
    } catch (e) {
      // no-op
    }
  }, [isEditing, isMobile, isIOS]);

  // Desktop/other: Use useEffect with setTimeout
  useEffect(() => {
    if (!isEditing) return;
    // Skip useLayoutEffect path for iOS mobile (handled above)
    if (isMobile && isIOS) return;
    const id = setTimeout(() => {
      weekNameInputRef.current?.focus?.();
    }, 0);
    return () => clearTimeout(id);
  }, [isEditing, isMobile, isIOS]);

  useEffect(() => {
    return () => {
      if (pendingBlurTimerRef.current) {
        clearTimeout(pendingBlurTimerRef.current);
        pendingBlurTimerRef.current = null;
      }
    };
  }, []);

  // Width reserved for the configure dots button on the last week
  const configureDotsWidth = 28;

  return (
    <View
      style={[
        styles.weekHeaderCell,
        { width: columnWidth },
        // For non-last weeks, apply border here; for last week, border is on inner container
        !isLastWeek && styles.weekHeaderCellBorder,
        !isLastWeek && isCurrentWeek && styles.weekHeaderCellCurrent,
        // Remove bottom padding for last week since inner container handles spacing
        isLastWeek && { paddingBottom: 0 },
      ]}
    >
      {/* Gradient for non-last weeks - placed in outer container where border is */}
      {isCurrentWeek && !isLastWeek && (
        <LinearGradient
          colors={[
            "rgba(63, 123, 141, 0.45)",
            "rgba(63, 123, 141, 0.25)",
            "rgba(63, 123, 141, 0.05)",
            "transparent",
          ]}
          locations={[0, 0.2, 0.5, 1]}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={styles.gradientBackground}
        />
      )}
      {/* Inner container: for the last week, this holds the bordered content */}
      <View
        style={[
          styles.weekHeaderInner,
          isLastWeek && styles.weekHeaderCellBorder,
          isLastWeek && isCurrentWeek && styles.weekHeaderCellCurrent,
          isLastWeek && { marginRight: configureDotsWidth, paddingBottom: 8 },
        ]}
      >
        {/* Gradient for last week - placed in inner container where border is */}
        {isCurrentWeek && isLastWeek && (
          <LinearGradient
            colors={[
              "rgba(63, 123, 141, 0.45)",
              "rgba(63, 123, 141, 0.25)",
              "rgba(63, 123, 141, 0.05)",
              "transparent",
            ]}
            locations={[0, 0.2, 0.5, 1]}
            start={{ x: 0, y: 1 }}
            end={{ x: 0, y: 0 }}
            style={styles.gradientBackground}
          />
        )}
        <View style={styles.weekHeaderMain}>
          <View style={styles.weekTitleRow}>
            <Tooltip
              content={
                isWeekHidden ? t("showWeek-string") : t("hideWeek-string")
              }
              placement="top"
            >
              <HoverableTouchableOpacity
                style={styles.hoverIconButton}
                hoverStyle={undefined}
                onPress={handleToggleHiddenWeek}
                title={
                  isWeekHidden ? t("showWeek-string") : t("hideWeek-string")
                }
                onMouseEnter={() => !isMobile && setIsEyeHovered(true)}
                onMouseLeave={() => !isMobile && setIsEyeHovered(false)}
              >
                <Feather
                  name={isWeekHidden ? "eye-off" : "eye"}
                  size={12}
                  color={
                    isWeekHidden
                      ? Colors.grayColor
                      : isCurrentWeek
                        ? Colors.bubbleColor
                        : Colors.darkgrayColor
                  }
                  style={{ opacity: isEyeHovered ? 0.7 : 1 }}
                />
              </HoverableTouchableOpacity>
            </Tooltip>

            {isEditing ? (
              <TextInput
                ref={weekNameInputRef}
                value={draft}
                onChangeText={setDraft}
                placeholder={getWeekLabel(week)}
                placeholderTextColor={Colors.grayColor}
                style={[
                  styles.weekTitleInput,
                  isMobile && isIOS && { fontSize: 16 },
                ]}
                onKeyDown={handleKey}
                onKeyPress={handleKey}
                onBlur={handleBlur}
                returnKeyType="done"
                onSubmitEditing={saveEdit}
                maxLength={40}
                selectTextOnFocus
              />
            ) : (
              <Text
                style={[
                  styles.weekTitle,
                  isCurrentWeek && styles.weekTitleCurrent,
                ]}
                numberOfLines={1}
              >
                {getWeekLabel(week)}
              </Text>
            )}

            {!isEditing &&
              (() => {
                const interval = formatWeekInterval(week);
                if (!interval) return null;
                return (
                  <Text style={styles.weekDateText} numberOfLines={1}>
                    · {interval}
                  </Text>
                );
              })()}
          </View>
        </View>

        <View style={styles.headerActions}>
          {isEditing ? (
            <>
              <HoverableTouchableOpacity
                style={styles.actionButton}
                hoverStyle={styles.actionButtonHovered}
                onPressIn={() => {
                  skipNextBlurCommitRef.current = true;
                  if (pendingBlurTimerRef.current) {
                    clearTimeout(pendingBlurTimerRef.current);
                    pendingBlurTimerRef.current = null;
                  }
                }}
                onPress={cancelEdit}
                title={t("cancel-string")}
              >
                <Feather name="x" size={14} color={Colors.grayColor} />
              </HoverableTouchableOpacity>

              <HoverableTouchableOpacity
                style={styles.actionButton}
                hoverStyle={styles.actionButtonHovered}
                onPress={saveEdit}
                title={t("save-string")}
              >
                <Ionicons
                  name="checkmark"
                  size={14}
                  color={Colors.bubbleColor}
                />
              </HoverableTouchableOpacity>
            </>
          ) : (
            <>
              <Tooltip content={t("editWeekName-string")} placement="top">
                <HoverableTouchableOpacity
                  style={styles.actionButton}
                  hoverStyle={styles.actionButtonHovered}
                  onPress={() => startEditingWeek(week)}
                  title={t("editWeekName-string")}
                >
                  <Feather name="edit-2" size={12} color={Colors.grayColor} />
                </HoverableTouchableOpacity>
              </Tooltip>

              {hasWorkouts && (
                <>
                  <Tooltip content={t("duplicateWeek-string")} placement="top">
                    <HoverableTouchableOpacity
                      style={styles.actionButton}
                      hoverStyle={styles.actionButtonHovered}
                      title={t("duplicateWeek-string")}
                      onPress={() =>
                        onDuplicateWeekPress && onDuplicateWeekPress(week)
                      }
                    >
                      <Ionicons
                        name="duplicate-outline"
                        size={13}
                        color={Colors.grayColor}
                      />
                    </HoverableTouchableOpacity>
                  </Tooltip>

                  <Tooltip
                    content={t("deleteWeekWorkoutsTooltip-string")}
                    placement="top"
                  >
                    <HoverableTouchableOpacity
                      style={styles.actionButton}
                      hoverStyle={styles.actionButtonTrashHovered}
                      onPress={() => onDeleteWeekPress(week)}
                      title={t("deleteWeekWorkoutsTooltip-string")}
                    >
                      <Ionicons
                        name="trash-outline"
                        size={13}
                        color={Colors.redColor}
                      />
                    </HoverableTouchableOpacity>
                  </Tooltip>
                </>
              )}
            </>
          )}
        </View>
      </View>

      {/* Configure weeks dots - only shown for last week, positioned outside the border */}
      {isLastWeek && onConfigureWeeks && (
        <View style={styles.configureDotsContainer}>
          <Tooltip content={t("configureWeeks-string")} placement="top">
            <HoverableTouchableOpacity
              style={styles.configureDotsButton}
              hoverStyle={styles.configureDotsButtonHovered}
              onPress={onConfigureWeeks}
              title={t("configureWeeks-string")}
            >
              <Ionicons
                name="settings-outline"
                size={14}
                color={Colors.grayColor}
              />
            </HoverableTouchableOpacity>
          </Tooltip>
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  weekHeaderCell: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 0,
    position: "relative",
  },
  weekHeaderCellBorder: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderColor,
  },
  weekHeaderCellCurrent: {
    borderBottomColor: Colors.bubbleColor,
    borderBottomWidth: 1,
    marginBottom: 0,
  },
  weekHeaderInner: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
  },
  gradientBackground: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "15%",
    zIndex: 0,
  },
  weekHeaderMain: {
    flex: 1,
    marginRight: 8,
    justifyContent: "center",
    zIndex: 1,
    position: "relative",
  },
  weekTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  weekTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
    flexShrink: 1,
  },
  weekTitleCurrent: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.bubbleColor,
    flexShrink: 1,
  },
  weekTitleInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
    padding: 0,
    outlineStyle: "none",
  },
  weekDateText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  headerActions: {
    flexDirection: "row",
    gap: 4,
    zIndex: 1,
    position: "relative",
  },
  hoverIconButton: {
    alignItems: "center",
    justifyContent: "center",
    padding: 4,
    borderRadius: 6,
  },
  hoverIconButtonHovered: {
    backgroundColor: Colors.whiteColor,
  },
  actionButton: {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    alignItems: "center",
    justifyContent: "center",
  },
  actionButtonHovered: {
    backgroundColor: Colors.whiteColor,
  },
  actionButtonTrashHovered: {
    backgroundColor: "rgba(240, 68, 56, 0.1)",
  },
  configureDotsContainer: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2,
  },
  configureDotsButton: {
    width: 24,
    height: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  configureDotsButtonHovered: {
    backgroundColor: "rgba(0, 0, 0, 0.06)",
  },
});

export default WeekHeader;
